import React from 'react'
import { Box, Container, Typography, Button, Stack } from '@mui/material'

export interface DonateHeroSectionProps {
  /**
   * Hero headline
   */
  title: string

  /**
   * Supporting subtitle text
   */
  subtitle?: string

  /**
   * Optional hero background image URL
   */
  imageUrl?: string

  /**
   * Alt text for hero image (required if imageUrl provided)
   */
  imageAlt?: string

  /**
   * CTA button text
   */
  ctaText?: string

  /**
   * Callback when CTA button is clicked
   * If not provided, button will scroll to contribution tiers
   */
  onCtaClick?: () => void
}

/**
 * Donate Hero Section Component
 *
 * Prominent hero banner for the donation page with responsive heights,
 * optional background imagery, and compelling call-to-action.
 *
 * **Responsive Heights:**
 * - Mobile (xs): 400px
 * - Tablet (sm): 450px
 * - Desktop (md+): 500px
 *
 * **Background:**
 * - If imageUrl provided: Full background image with dark overlay for text readability
 * - If no imageUrl: Solid primary color background
 *
 * **Specifications:**
 * - Recommended image: 1920x1080px minimum, 16:9 aspect ratio
 * - Format: WebP preferred, JPEG acceptable
 * - Size: <200KB for optimal loading
 *
 * @example
 * ```tsx
 * <DonateHeroSection
 *   title="Help Keep Families Together"
 *   subtitle="Support equal shared parenting legislation"
 *   imageUrl="/images/hero/donate-hero.jpg"
 *   imageAlt="Family advocating for equal parenting"
 *   ctaText="Donate Now"
 * />
 * ```
 */
export const DonateHeroSection: React.FC<DonateHeroSectionProps> = ({
  title,
  subtitle,
  imageUrl,
  imageAlt,
  ctaText = 'Donate Now',
  onCtaClick,
}) => {
  /**
   * Handle CTA click - either use provided callback or scroll to tiers section
   */
  const handleCtaClick = () => {
    if (onCtaClick) {
      onCtaClick()
    } else {
      // Scroll to contribution tiers section
      const tiersSection = document.getElementById('contribution-tiers-section')
      if (tiersSection) {
        tiersSection.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }
  }

  return (
    <Box
      component="section"
      sx={{
        position: 'relative',
        height: { xs: 600, md: 700 },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        // Background image or fallback to primary color
        ...(imageUrl
          ? {
              backgroundImage: `url(${imageUrl})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
            }
          : {
              bgcolor: 'primary.main',
            }),
      }}
      aria-label="Donation page hero banner"
    >
      {/* Dark overlay for text readability when image is present */}
      {imageUrl && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            bgcolor: 'rgba(33, 30, 76, 0.6)', // Navy overlay at 60% opacity
          }}
          aria-hidden="true"
        />
      )}

      {/* Hero Content */}
      <Container
        maxWidth="lg"
        sx={{
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Stack
          spacing={{ xs: 3, sm: 4 }}
          alignItems="center"
          textAlign="center"
          sx={{
            px: { xs: 2, sm: 4 },
          }}
        >
          {/* Hero Title */}
          <Typography
            variant="h1"
            component="h1"
            sx={{
              fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4rem' },
              fontWeight: 700,
              color: 'white',
              textShadow: imageUrl ? '0 2px 4px rgba(0,0,0,0.3)' : 'none',
              maxWidth: '900px',
              lineHeight: 1.2,
            }}
          >
            {title}
          </Typography>

          {/* Hero Subtitle */}
          {subtitle && (
            <Typography
              variant="h2"
              component="p"
              sx={{
                fontSize: { xs: '1.125rem', sm: '1.375rem', md: '1.5rem' },
                fontWeight: 400,
                color: 'white',
                textShadow: imageUrl ? '0 2px 4px rgba(0,0,0,0.3)' : 'none',
                maxWidth: '700px',
                lineHeight: 1.6,
              }}
            >
              {subtitle}
            </Typography>
          )}

          {/* CTA Button */}
          <Button
            variant="contained"
            size="large"
            onClick={handleCtaClick}
            aria-label={`${ctaText} - Navigate to contribution tiers`}
            sx={{
              py: 2,
              px: 6,
              fontSize: { xs: '1.125rem', sm: '1.25rem' },
              fontWeight: 600,
              minHeight: 56, // WCAG touch target
              minWidth: 200,
              textTransform: 'none',
              bgcolor: 'secondary.main', // Red CTA for contrast
              color: 'white',
              boxShadow: 3,
              '&:hover': {
                bgcolor: 'secondary.dark',
                boxShadow: 6,
                transform: 'translateY(-2px)',
              },
              transition: 'all 0.3s ease-in-out',
            }}
          >
            {ctaText}
          </Button>
        </Stack>
      </Container>

      {/* Screen reader alt text for background image */}
      {imageUrl && imageAlt && (
        <Box
          component="img"
          src={imageUrl}
          alt={imageAlt}
          sx={{
            position: 'absolute',
            width: 0,
            height: 0,
            opacity: 0,
          }}
          aria-hidden="false"
        />
      )}
    </Box>
  )
}
